import type {
  CreativeBoard,
  CreativeContextSelection,
  CreativeItem,
  CreativeProduct,
  CreativeSubject,
} from "@digital-twins/shared-types";
import {
  creativeSelectionFromSummary,
  sameCreativeSelection,
} from "@digital-twins/shared-types";
import type { AssistantImageRequest } from "@digital-twins/shared-types/assistant";

/** What the card starts from: the choices the quote was built with. */
export function selectionFromRequest(
  request: Pick<AssistantImageRequest, "contextSummary">,
): CreativeContextSelection {
  return creativeSelectionFromSummary(request.contextSummary);
}

export const sameSelection = sameCreativeSelection;

/** Something the brief was written for was replaced; a filled empty slot is only a tag. */
export function isSwap(
  quoted: CreativeContextSelection,
  next: CreativeContextSelection,
) {
  return (
    ["brandProfileToken", "subjectToken", "productToken"] as const
  ).some((slot) => quoted[slot] && quoted[slot] !== (next[slot] ?? null));
}

export function withBrand(
  selection: CreativeContextSelection,
  brandToken: string | null,
  library: {
    boards: CreativeBoard[];
    products: CreativeProduct[];
    subjects: CreativeSubject[];
  },
): CreativeContextSelection {
  const board = library.boards.find(
    (item) => item.token === selection.boardToken,
  );
  const keepBoard =
    brandToken &&
    board &&
    (board.brandToken === brandToken ||
      board.applicableBrandTokens.includes(brandToken));
  const keepProduct = library.products.some(
    (item) =>
      item.token === selection.productToken && item.brandToken === brandToken,
  );
  const keepSubject = library.subjects.some(
    (item) =>
      item.token === selection.subjectToken && item.brandToken === brandToken,
  );
  return {
    ...selection,
    brandProfileToken: brandToken,
    productToken: keepProduct ? selection.productToken : null,
    subjectToken: keepSubject ? selection.subjectToken : null,
    boardToken: keepBoard ? selection.boardToken : null,
    channel: keepBoard ? selection.channel : null,
    referenceTokens: [],
  };
}

export function withBoard(
  selection: CreativeContextSelection,
  boardToken: string | null,
  boards: CreativeBoard[],
): CreativeContextSelection {
  const board = boards.find((item) => item.token === boardToken);
  const left = boards.find((item) => item.token === selection.boardToken)?.definition?.referenceTokens ?? [];
  return {
    ...selection,
    boardToken,
    channel: null,
    // The card holds every image a quote renders, so a Board's own go with it.
    referenceTokens: (selection.referenceTokens ?? []).filter((token) => !left.includes(token)),
    ...(!selection.brandProfileToken && board
      ? { brandProfileToken: board.brandToken }
      : {}),
  };
}

export function withProduct(
  selection: CreativeContextSelection,
  productToken: string | null,
  creatives: CreativeItem[],
): CreativeContextSelection {
  return {
    ...selection,
    productToken,
    referenceTokens: (selection.referenceTokens ?? []).filter((token) => {
      const item = creatives.find((candidate) => candidate.token === token);
      return !(
        item?.role === "product" &&
        item.productToken &&
        item.productToken !== productToken
      );
    }),
  };
}

export function withSubject(
  selection: CreativeContextSelection,
  subjectToken: string | null,
  library: { subjects: CreativeSubject[]; creatives: CreativeItem[] },
): CreativeContextSelection {
  const current = library.subjects.find(
    (item) => item.token === selection.subjectToken,
  );
  const next = library.subjects.find((item) => item.token === subjectToken);
  return {
    ...selection,
    subjectToken,
    referenceTokens: (selection.referenceTokens ?? []).filter((token) => {
      const image = library.creatives.find((item) => item.token === token);
      const identity =
        image?.role === "identity" || current?.referenceTokens.includes(token);
      return !identity || Boolean(next?.referenceTokens.includes(token));
    }),
  };
}
